import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clearAuthAndRedirectToLogin } from '../lib/authSession';
import { prefetchMainPages } from '../lib/prefetch';
import { clearPageCache } from '../lib/pageCache';
import { identifyUser, clearUser, track } from '../lib/analytics';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? '/api'
    : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000') + '/api';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  roleDisplayName?: string;
  isActive: boolean;
  permissions?: Record<
    string,
    { view: boolean; create: boolean; edit: boolean; delete: boolean }
  >;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

const LS_TOKEN_KEY = 'token';
const LS_USER_KEY = 'user';

const EXTRA_AUTH_KEYS = [
  'auth',
  'authToken',
  'accessToken',
  'jwt',
  'jwtToken',
  'refreshToken',
  'userData',
  'persist:root',
];

function safeJsonParse<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function isPublicAuthPath(url: string): boolean {
  return (
    url.includes('/users/login') ||
    url.includes('/users/forgot-password') ||
    url.includes('/users/reset-password')
  );
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hydrate = () => {
    const t = localStorage.getItem(LS_TOKEN_KEY);
    const u = safeJsonParse<User>(localStorage.getItem(LS_USER_KEY));
    if (t && u) {
      setToken(t);
      setUser(u);
      identifyUser(u?.role, u?.id || (u as any)?._id);
    } else {
      setToken(null);
      setUser(null);
    }
  };

  useEffect(() => {
    hydrate();
    // Existing session on load (e.g. a page refresh) — warm the board cache too.
    if (localStorage.getItem(LS_TOKEN_KEY)) prefetchMainPages();

    const originalFetch = window.fetch.bind(window);
    (window as any).fetch = async (...args: any[]) => {
      const res: Response = await originalFetch(...(args as Parameters<typeof fetch>));
      if (res && res.status === 401) {
        const raw = args[0];
        const url = typeof raw === 'string' ? raw : (raw as Request)?.url || '';
        if (!isPublicAuthPath(url)) {
          clearAuthAndRedirectToLogin();
          setUser(null);
          setToken(null);
        }
      }
      return res;
    };

    (async () => {
      const t = localStorage.getItem(LS_TOKEN_KEY);
      if (t) {
        try {
          const res = await originalFetch(`${API_BASE_URL}/users/profile`, {
            headers: { Authorization: `Bearer ${t}` },
          });
          if (res.ok) {
            const json = await res.json();
            const fresh = json.data as User;
            setUser(fresh);
            setToken(t);
            localStorage.setItem(LS_USER_KEY, JSON.stringify(fresh));
          }
        } catch {
          /* ignore */
        }
      }
      setIsLoading(false);
    })();

    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      const watched = new Set([LS_TOKEN_KEY, LS_USER_KEY, ...EXTRA_AUTH_KEYS]);
      if (watched.has(e.key)) hydrate();
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      (window as any).fetch = originalFetch;
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        track('login_failed', { error_type: 'invalid_credentials', status: res.status });
        return false;
      }

      const data = await res.json();
      const { token: authToken, user: userData } = data.data;

      setToken(authToken);
      setUser(userData);
      identifyUser(userData?.role, userData?.id || (userData as any)?._id);
      track('login', { method: 'password' });

      localStorage.setItem(LS_TOKEN_KEY, authToken);
      localStorage.setItem(LS_USER_KEY, JSON.stringify(userData));

      // Warm the dashboard/board data now so the first screen after login is instant.
      prefetchMainPages();

      return true;
    } catch (err) {
      // Network/fetch failure (e.g. "Failed to fetch") — the request never reached
      // the server, so this is NOT a credential problem. Re-throw so the caller can
      // show a network-specific message. A 401 (wrong credentials) still returns
      // false above and shows "Invalid email or password".
      // eslint-disable-next-line no-console
      console.error('Login error:', err);
      throw err;
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(LS_TOKEN_KEY);
      localStorage.removeItem(LS_USER_KEY);
      for (const k of EXTRA_AUTH_KEYS) localStorage.removeItem(k);
      sessionStorage.clear();
      clearPageCache(); // don't leak one user's cached lists into the next session
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Logout storage clear error:', err);
    }

    setUser(null);
    setToken(null);
    clearUser();
    track('logout');
  };

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated: !!user && !!token,
      isLoading,
    }),
    [user, token, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
